import { useMemo } from 'react';
import { useAppSelector } from 'hooks/typedHooks';
import { FSMPlan, FSMPlanStatus } from 'containers/integrations/fsm-integration/constants';
import { selectSubscriptionDetails, selectSubscriptionPlans } from '../fsmSubscriptionPlansSlice';
import DateUtils from 'utils/dateUtils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { APPLE_SUBSCRIPTION_URL, GOOGLE_PLAY_SUBSCRIPTION_URL } from 'config/index';

interface PlanStates {
  isFSMLitePlan: boolean;
  isFSMPlusPlan: boolean;
  isNoPlan: boolean;
  isInTrial: boolean;
  isSubscriptionFromWeb: boolean;
}

interface PlanStatus {
  isTrialEnded: boolean;
  isUserCancelled: boolean;
  isSystemDowngraded: boolean;
  hasDowngradedAfterTrial: boolean;
  hasPaymentFailed: boolean;
  isSubscriptionConfirmed: boolean;
  isPaymentMethodUpdated: boolean | undefined;
  isPlanActive: boolean;
  isInFSMGracePeriod: boolean;
  migratedTeamWithJobs: boolean;
  collectPaymentMethodInfoAfterConfirmation: boolean;
}

interface DateInfo {
  fsmPlanValidUntilDate: string;
  gracePeriodEndsAtDate: string;
  trialPeriodEndsAtDate: string;
}

interface PlanDetails {
  trialDuration: number;
  price: number;
  formattedPrice: string;
  priceId: string;
}

interface FSMPlanInfo extends PlanStates, PlanStatus, DateInfo {
  currentFSMPlan: FSMPlan | undefined;
  status: string | undefined;
  fsmPlanValidUntil: string | undefined;
  gracePeriodEndsAt: string | undefined;
  trialPeriodEndsAt: string | undefined;
  freePlanFrom: string;
  fsmLitePlan: PlanDetails | null;
  fsmPlusPlan: PlanDetails | null;
  hasExpiredPlan: boolean;
  source: string | undefined;
  storeRedirectUrl: string | undefined;
  isActiveFSMPlusPlan: boolean;
}

const useFSMPlanInfo = (): FSMPlanInfo => {
  const subscriptionDetails = useAppSelector(selectSubscriptionDetails);
  const subscriptionPlans = useAppSelector(selectSubscriptionPlans);

  const currentFSMPlan = subscriptionDetails?.currentFSMPlan;
  const statusString = subscriptionDetails?.status;
  const fsmPlanValidUntil = subscriptionDetails?.fsmPlanValidUntil;
  const isPaymentMethodUpdated = subscriptionDetails?.isPaymentMethodUpdated;
  const gracePeriodEndsAt = subscriptionDetails?.gracePeriodEndsAt;
  const trialPeriodEndsAt = subscriptionDetails?.trialPeriodEndsAt;
  const isTrialEnded = subscriptionDetails?.isTrialEnded;
  const isUserCancelled = subscriptionDetails?.isUserCancelled;
  const isSubscriptionConfirmed = subscriptionDetails?.isSubscriptionConfirmed;
  const freePlanFrom = subscriptionDetails?.freePlanFrom;
  const stripeSubscriptionId = subscriptionDetails?.stripeSubscriptionId;

  const status: string | undefined = statusString;

  const formatDate = (dateString: string | undefined): string => {
    return dateString ? DateUtils.getDateInFormat({ date: new Date(dateString), dateFormat: 'MMM dd, yyyy' }) : '';
  };

  const planStates: PlanStates = useMemo(
    () => ({
      isFSMLitePlan: currentFSMPlan === FSMPlan.FSM_LITE,
      isFSMPlusPlan: currentFSMPlan === FSMPlan.FSM_PLUS,
      isNoPlan: currentFSMPlan === FSMPlan.NO_PLAN || !currentFSMPlan,
      isInTrial: subscriptionDetails?.isInTrial ?? false,
      isSubscriptionFromWeb: subscriptionDetails?.source?.toLowerCase() === 'web'
    }),
    [subscriptionDetails]
  );

  const planStatus: PlanStatus = useMemo(
    () => ({
      isTrialEnded: isTrialEnded ?? false,
      isUserCancelled: isUserCancelled ?? false,
      isSystemDowngraded: status?.toUpperCase() === FSMPlanStatus.SYSTEM_DOWNGRADE,
      hasDowngradedAfterTrial: status?.toUpperCase() === FSMPlanStatus.TRIAL_ENDED,
      hasPaymentFailed: status?.toUpperCase() === FSMPlanStatus.PAYMENT_FAILED,
      isSubscriptionConfirmed: isSubscriptionConfirmed ?? false,
      collectPaymentMethodInfoAfterConfirmation: !stripeSubscriptionId && isSubscriptionConfirmed,
      isPaymentMethodUpdated,
      isPlanActive: status?.toUpperCase() === FSMPlanStatus.ACTIVE || status?.toUpperCase() === FSMPlanStatus.TRIAL,
      isInFSMGracePeriod: subscriptionDetails?.isInFSMGracePeriod ?? false,
      migratedTeamWithJobs: subscriptionDetails?.migratedTeamWithJobs ?? false
    }),
    [subscriptionDetails]
  );

  const dateInfo: DateInfo = useMemo(() => {
    return {
      fsmPlanValidUntilDate: formatDate(fsmPlanValidUntil),
      gracePeriodEndsAtDate: formatDate(gracePeriodEndsAt),
      trialPeriodEndsAtDate: formatDate(trialPeriodEndsAt)
    };
  }, [fsmPlanValidUntil, gracePeriodEndsAt, trialPeriodEndsAt]);

  const hasExpiredPlan = useMemo(() => {
    return (
      planStatus.isSystemDowngraded ||
      planStatus.hasDowngradedAfterTrial ||
      (planStatus.isTrialEnded && gracePeriodEndsAt && new Date(gracePeriodEndsAt) < new Date())
    );
  }, [planStatus]);

  const { fsmLitePlan, fsmPlusPlan } = useMemo(() => {
    let litePlan: PlanDetails | null = null;
    let plusPlan: PlanDetails | null = null;

    if (subscriptionPlans && subscriptionPlans.length > 0) {
      const litePlanData = subscriptionPlans.find(plan => plan.name?.toLowerCase().includes('fsm_lite_web'));

      const plusPlanData = subscriptionPlans.find(plan => plan.name?.toLowerCase().includes('fsm_plus'));

      if (litePlanData) {
        litePlan = {
          trialDuration: litePlanData.trialDuration ?? 0,
          price: litePlanData.price ? litePlanData.price / 100 : 0,
          formattedPrice: getAmountWithCurrency(((litePlanData.price ?? 0) / 100).toString()),
          priceId: litePlanData.code
        };
      }

      if (plusPlanData) {
        plusPlan = {
          trialDuration: plusPlanData.trialDuration ?? 0,
          price: plusPlanData.price ? plusPlanData.price / 100 : 0,
          formattedPrice: getAmountWithCurrency(((plusPlanData.price ?? 0) / 100).toString()),
          priceId: plusPlanData.code
        };
      }
    }

    return { fsmLitePlan: litePlan, fsmPlusPlan: plusPlan };
  }, [subscriptionPlans]);

  const source = useMemo(() => {
    const sourceValue = subscriptionDetails?.source;
    let sourceRedirectUrl = '';
    if (sourceValue === 'ios') {
      sourceRedirectUrl = APPLE_SUBSCRIPTION_URL;
    } else if (sourceValue === 'android') {
      sourceRedirectUrl = GOOGLE_PLAY_SUBSCRIPTION_URL;
    } else {
      sourceRedirectUrl = '';
    }
    if (!sourceValue) return { source: '', storeRedirectUrl: '' };
    return { source: sourceValue.toLowerCase(), storeRedirectUrl: sourceRedirectUrl };
  }, [subscriptionDetails?.source]);

  const isActiveFSMPlusPlan = useMemo(() => {
    return currentFSMPlan === FSMPlan.FSM_PLUS && !hasExpiredPlan;
  }, [currentFSMPlan, hasExpiredPlan]);

  return {
    hasExpiredPlan,
    currentFSMPlan,
    status,
    fsmPlanValidUntil,
    gracePeriodEndsAt,
    trialPeriodEndsAt,
    freePlanFrom,
    fsmLitePlan,
    fsmPlusPlan,
    ...source,
    ...planStates,
    ...planStatus,
    ...dateInfo,
    isActiveFSMPlusPlan
  };
};

export default useFSMPlanInfo;
